// Kurum paneli (B02–B13, B20, B25, B26): kuruma özgü girdi ve yanıt tipleri. Okuma uçları Control ile ortaktır (kiracı
// katmanı kurumu kendi sözleşmeli seferleri ve yolcularıyla sınırlar); kurumun yazma uçları `/v1/institution/*` altındadır.
import { z } from 'zod';
import { PASSENGER_KINDS } from '../enums';
import type { AnnouncementTarget } from './business';
import { RosterGuardianInput, type RosterInviteResult } from './roster';

// ───────── B09 çalışan / öğrenci ekle ─────────

const Name = z.string().trim().min(1, 'Zorunlu alan').max(60);
const Opt = (max: number) => z.string().trim().max(max).nullable().optional();

/** Kurum kendi yolcusunu ekler: kurum kimliği bağlamdan gelir (başka kuruma yolcu eklenemez). */
export const InstitutionPassengerInput = z.object({
  kind: z.enum(PASSENGER_KINDS).default('EMPLOYEE'),
  firstName: Name,
  lastName: Name,
  /** serbest giriş; sunucu E.164'e çevirir (+905xxxxxxxxx). Öğrencide isteğe bağlı. */
  phone: Opt(20),
  /**
   * Yalnız davet e-postası (E02) için; hesaba yazılmaz. Hesapta kayıtlı e-posta varsa o kullanılır.
   */
  email: z.string().trim().email('Geçerli bir e-posta adresi yazın.').max(120).nullable().optional(),
  externalId: Opt(40),
  groupId: z.string().max(64).nullable().optional(),
  className: Opt(20),
  homeAddress: Opt(200),
  homeLat: z.number().min(-90).max(90).nullable().optional(),
  homeLng: z.number().min(-180).max(180).nullable().optional(),
  morningStopId: z.string().max(64).nullable().optional(),
  eveningStopId: z.string().max(64).nullable().optional(),
  /** yalnız öğrenci + Okul modülü */
  guardians: z.array(RosterGuardianInput).max(4).optional(),
  /** kayıttan hemen sonra davet SMS'i (öğrencide velilere) ve varsa e-posta */
  sendInvite: z.boolean().default(true),
});
export type InstitutionPassengerInput = z.input<typeof InstitutionPassengerInput>;

export interface InstitutionInviteSummary {
  /** gönderilen davet SMS'i (öğrencide veli sayısı kadar) */
  sms: number;
  /** davet e-postası (E02) gönderildi mi */
  email: boolean;
}

export interface InstitutionPassengerCreated {
  id: string;
  invite: InstitutionInviteSummary | null;
}

/** Kurumun kendi yolcusunda değiştirebildiği alanlar: kayıt durumu (ayrılan çalışan) ve grup. */
export const InstitutionPassengerUpdate = z
  .object({
    status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
    groupId: z.string().max(64).nullable().optional(),
  })
  .refine((v) => v.status !== undefined || v.groupId !== undefined, 'Değiştirilecek bir alan seçin.');
export type InstitutionPassengerUpdate = z.infer<typeof InstitutionPassengerUpdate>;

export const InstitutionInviteInput = z.object({ ids: z.array(z.string().max(64)).min(1).max(500) });

export interface InstitutionInviteResult extends RosterInviteResult {
  /** gönderilen davet e-postası (yalnız hesabında e-postası olan çalışanlar) */
  emails: number;
}

// ───────── B11 adres / talep onayı ─────────

/**
 * Kurum onayı: yalnız "Kurum onayında" durumundaki talep. Onayda (atama yapan taleplerde) durak kurumun rotalarından
 * seçilir; ret gerekçesi talep sahibine bildirilir.
 */
export const InstitutionRequestDecisionInput = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('APPROVE'),
    stopId: z.string().max(64).optional(),
    directions: z
      .array(z.enum(['OUTBOUND', 'INBOUND']))
      .min(1)
      .max(2)
      .default(['OUTBOUND', 'INBOUND']),
    note: z.string().trim().max(300).optional(),
  }),
  z.object({
    action: z.literal('REJECT'),
    reason: z.string().trim().min(5, 'Ret gerekçesini en az 5 harfle yazın.').max(300),
  }),
]);
export type InstitutionRequestDecisionInput = z.input<typeof InstitutionRequestDecisionInput>;

// ───────── B25 duyurular ─────────

/** Kurumun duyuru hedefleri: yalnız kendi yolcuları (öğrencide veliler). Şoför, araç ve başka kurum hedeflenemez. */
export const INSTITUTION_ANNOUNCEMENT_TARGETS = [
  'PASSENGERS',
  'ROUTE',
  'GROUP',
  'PARENTS',
] as const satisfies readonly AnnouncementTarget[];
export type InstitutionAnnouncementTarget = (typeof INSTITUTION_ANNOUNCEMENT_TARGETS)[number];

export const INSTITUTION_ANNOUNCEMENT_TARGET_LABELS: Record<InstitutionAnnouncementTarget, string> = {
  PASSENGERS: 'Tüm yolcular',
  ROUTE: 'Rota',
  GROUP: 'Grup',
  PARENTS: 'Veliler',
};

export const INSTITUTION_ANNOUNCEMENT_TARGET_HINTS: Record<InstitutionAnnouncementTarget, string> = {
  PASSENGERS: 'Kurumunuzun tüm aktif yolcuları; öğrencilerde veliler.',
  ROUTE: 'Seçtiğiniz servis rotasındaki yolcular.',
  GROUP: 'Departman, sınıf, vardiya veya bina grubu.',
  PARENTS: 'Yalnız öğrenci velileri.',
};

export function isInstitutionTarget(t: string): t is InstitutionAnnouncementTarget {
  return (INSTITUTION_ANNOUNCEMENT_TARGETS as readonly string[]).includes(t);
}
